import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

const IDLE_LOGOUT_MS = 30 * 60 * 1000;          // sign out after 30 min idle
const WARN_AT_MS = IDLE_LOGOUT_MS - 5 * 60 * 1000; // first warning: 5 min before
const WARN2_AT_MS = IDLE_LOGOUT_MS - 2 * 60 * 1000; // second warning: 2 min before

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = auth
  const [ready, setReady] = useState(false);
  const [idleWarn, setIdleWarn] = useState(false);
  const [idleSeconds, setIdleSeconds] = useState(0);

  const lastActivity = useRef(Date.now());
  const inWarn = useRef(false);
  const warned = useRef({ five: false, two: false });

  useEffect(() => {
    const token = localStorage.getItem("cs_token");
    if (!token) {
      setUser(false);
      setReady(true);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => {
        localStorage.removeItem("cs_token");
        setUser(false);
      })
      .finally(() => setReady(true));
  }, []);

  const applyAuth = useCallback((token, u) => {
    localStorage.setItem("cs_token", token);
    lastActivity.current = Date.now();
    inWarn.current = false;
    warned.current = { five: false, two: false };
    setIdleWarn(false);
    setUser(u);
  }, []);

  const refreshUser = useCallback(async () => {
    const res = await api.get("/auth/me");
    setUser(res.data);
    return res.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("cs_token");
    inWarn.current = false;
    warned.current = { five: false, two: false };
    setIdleWarn(false);
    setUser(false);
  }, []);

  const stayActive = useCallback(() => {
    lastActivity.current = Date.now();
    inWarn.current = false;
    warned.current = { five: false, two: false };
    setIdleWarn(false);
  }, []);

  // Inactivity watchdog: warn at 5 & 2 min, then auto sign-out at 30 min idle.
  useEffect(() => {
    if (!user) return;
    const bump = () => {
      if (inWarn.current) return; // during warning, only "Stay signed in" resets the timer
      lastActivity.current = Date.now();
      warned.current = { five: false, two: false };
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));

    const iv = setInterval(() => {
      const idle = Date.now() - lastActivity.current;
      if (idle >= IDLE_LOGOUT_MS) {
        logout();
        toast.error("Signed out due to 30 minutes of inactivity.");
        return;
      }
      if (idle >= WARN_AT_MS) {
        inWarn.current = true;
        setIdleWarn(true);
        setIdleSeconds(Math.ceil((IDLE_LOGOUT_MS - idle) / 1000));
        if (!warned.current.five) {
          warned.current.five = true;
          toast.warning("You will be signed out in 5 minutes due to inactivity.");
        }
        if (idle >= WARN2_AT_MS && !warned.current.two) {
          warned.current.two = true;
          toast.warning("Only 2 minutes left before automatic sign-out.");
        }
      } else if (inWarn.current) {
        inWarn.current = false;
        setIdleWarn(false);
      }
    }, 1000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      clearInterval(iv);
    };
  }, [user, logout]);

  return (
    <AuthContext.Provider value={{ user, ready, idleWarn, idleSeconds, stayActive, applyAuth, refreshUser, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
